import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import ReactGA from 'react-ga4'
import { Parallax } from 'react-scroll-parallax'
import { toolGroups, toolHeading } from '../data/tools'
import Masonry, { ResponsiveMasonry } from './Masonry'

export default function Tools() {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  const toggle = (id: string) => {
    setExpanded((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  return (
    <section
      id="tools"
      className="container mx-auto max-w-7xl px-0 py-0 sm:px-4 sm:py-4"
    >
      <div className="fit-content relative overflow-hidden">
        <div className="fit-content relative px-4 py-8">
          {/* Parallaxed image */}
          <Parallax
            translateY={[-20, 10]}
            disabled={false}
            className="absolute inset-0 z-0"
          >
            <picture>
              <source
                type="image/webp"
                srcSet="/media/hero/studio-header_320.webp 320w, 
                /media/hero/studio-header_480.webp 480w, 
                /media/hero/studio-header_640.webp 640w, 
                /media/hero/studio-header_768.webp 768w, 
                /media/hero/studio-header_960.webp 960w, 
                /media/hero/studio-header_1200.webp 1200w"
                sizes="100vw"
              />
              <img
                src="/media/hero/studio-header_640.webp"
                alt="Coders at work"
                className="h-full w-full scale-110 object-cover"
                loading="lazy"
                decoding="async"
              />
            </picture>
          </Parallax>

          {/* Gradient overlay - stronger for better text contrast */}
          <div
            className="bg-linear-to-t pointer-events-none absolute inset-0 z-10 from-transparent via-white/70 to-gray-300 dark:via-black/70 dark:to-gray-900"
            aria-hidden="true"
          />
          <div className="relative z-50 mb-8 max-w-md">
            {toolHeading.heading && (
              <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                {toolHeading.heading}
              </h2>
            )}
            {toolHeading.paragraph && (
              <p className="mt-3 text-neutral-600 dark:text-neutral-300">
                {toolHeading.paragraph}
              </p>
            )}
          </div>

          <ResponsiveMasonry
            {...{
              columnsCountBreakPoints: {
                320: 1,
                768: 2,
                960: 3,
              },
              className: 'relative z-20',
              gutterBreakPoints: {
                320: '1rem',
                768: '1rem',
                960: '1rem',
              },
            }}
          >
            <Masonry>
              {toolGroups.map((group) => (
                <article
                  key={group.id}
                  className={`group relative mb-0 h-fit cursor-pointer rounded-xl border-2 p-5 shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg ${expanded[group.id] ? 'border-brand-500/30 bg-white dark:bg-neutral-900' : 'border-white/50 bg-white/90 backdrop-blur-sm dark:border-neutral-700/50 dark:bg-neutral-900/90'}`}
                  // data-aos="fade-up-left"
                  onClick={(e) => {
                    e.preventDefault()
                    toggle(group.id)
                    ReactGA.event({
                      category: 'Button Click',
                      action: `User clicked to expand: ${group.title}`,
                      label: 'Tools Section',
                    })
                  }}
                >
                  <div
                    className={`transition-all duration-300 ${expanded[group.id] ? 'h-0 overflow-hidden opacity-0' : 'opacity-100'}`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-500/10 text-brand-600 dark:bg-brand-500/20 dark:text-brand-400"
                        aria-hidden
                      >
                        <FontAwesomeIcon
                          icon={group.icon}
                          className="h-5 w-5"
                        />
                      </span>
                      <h3 className="text-lg font-semibold leading-tight text-neutral-900 dark:text-white">
                        {group.title}
                      </h3>
                    </div>
                    <p className="text-sm mt-3 leading-relaxed text-neutral-600 dark:text-neutral-400">
                      {group.summary}
                    </p>
                  </div>

                  <ul
                    className={`text-sm list-none space-y-2 leading-relaxed text-neutral-700 transition-all duration-300 dark:text-neutral-300 ${expanded[group.id] ? 'mt-1 opacity-100' : 'h-0 overflow-hidden opacity-0'}`}
                  >
                    {group.details.map((detail) => (
                      <li className="flex gap-2" key={detail}>
                        <span
                          className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500"
                          aria-hidden
                        />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </Masonry>
          </ResponsiveMasonry>
        </div>
      </div>
    </section>
  )
}
